// @story HIR-HR1
// CareersPublishCard — puts a vacancy on the public careers site, or pauses it
// (takes it down without deleting anything), and sets its closing date: the
// last day it takes applications, after which it leaves the careers page.
//
// Writes job.careers via PUT /api/jobs/:jobId/careers. The recruitment landing
// page reads GET /api/public/careers and folds published vacancies into its
// open-positions directory, so everything on this card is public the moment
// the toggle goes on.
//
// Publishing is deliberately separate from the job's workflow status: a
// vacancy can be "Incomplete" here and still be advertised, or finished and
// deliberately unlisted.

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { Button, Card, CardHeader, FormField, Icon, Input } from '../../ds';
import { Note } from '../../components/page';

// Where the public careers directory lives. #positions is the landing page's
// own deep link to its Open Positions tab.
const CAREERS_PAGE_URL = 'https://recruit.dpspuneeast.com/#positions';

// The vacancy's track, as the server's config/vacancyOptions.js lists it.
const TRACKS = [
  { value: '', label: 'Not stated' },
  { value: 'teaching', label: 'Teaching' },
  { value: 'non-teaching', label: 'Non-teaching' },
  { value: 'leadership', label: 'Leadership' },
];

// @story HIR-HR1
// The closing instant is the end of that day in India; show and edit the day.
const dayOf = (closesAt) => (closesAt
  ? new Date(new Date(closesAt).getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10)
  : '');
const longDay = (d) => (d ? new Date(`${d}T12:00:00+05:30`).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

export default function CareersPublishCard({ jobId, careers, hasDescription, onSaved }) {
  const [form, setForm] = useState({
    published: !!careers?.published,
    openings:  careers?.openings || 1,
    category:  careers?.category || '',
    gradeBand: careers?.gradeBand || '',
    tags:      careers?.tags || '',
    track:     careers?.track || '',
    closingDate: dayOf(careers?.closesAt),
  });
  const [paused, setPaused] = useState({ paused: !!careers?.paused, at: careers?.pausedAt || null, by: careers?.pausedBy?.name || '' });
  const today = new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
  const closed = !!form.closingDate && form.closingDate < today;
  const [publishedAt, setPublishedAt] = useState(careers?.publishedAt || null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty]   = useState(false);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/jobs/${jobId}/careers`, {
        ...form,
        openings: Number(form.openings) || 1,
      });
      const saved = res.data?.data?.careers;
      if (saved) {
        setForm((f) => ({ ...f, ...saved, published: !!saved.published, closingDate: dayOf(saved.closesAt) }));
        setPublishedAt(saved.publishedAt || null);
        setPaused({ paused: !!saved.paused, at: saved.pausedAt || null, by: saved.pausedBy?.name || '' });
      }
      setDirty(false);
      toast.success(form.published ? 'Vacancy is live on the careers site' : 'Vacancy paused — off the careers site, nothing deleted');
      if (onSaved) onSaved(saved);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save publication settings');
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, props = {}) => (
    <Input
      id={`careers-${key}`}
      label={label}
      value={form[key]}
      onChange={(e) => set({ [key]: e.target.value })}
      {...props}
    />
  );
  // A native input drawn as the design system's Input, for the two fields
  // that need the browser's own min (posts, closing date).
  const nativeInput = {
    height: 40, padding: '0 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)',
    background: 'var(--surface-1)', color: 'var(--text-1)', font: 'inherit', fontSize: 'var(--fs-body-md)', width: '100%', boxSizing: 'border-box',
  };
  const caption = { margin: 0, fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' };

  return (
    <Card>
      <CardHeader title="Careers site" />

      <div style={{ display: 'grid', gap: 20 }}>
        {/* ── The switch ─────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24 }}>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>
              {form.published ? 'Published' : 'Publish'}
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)', maxWidth: 520 }}>
              {form.published
                ? 'This vacancy and its job description are visible to anyone on the public careers page.'
                : 'Turn this on to advertise the vacancy. The job description below is what candidates will read.'}
            </p>
          </div>

          {/* Drawn as the design system's Switch; a real switch for screen readers. */}
          <button
            type="button"
            role="switch"
            aria-checked={form.published}
            aria-label="List this vacancy on the careers site"
            onClick={() => set({ published: !form.published })}
            style={{
              position: 'relative', flex: 'none', width: 40, height: 24, borderRadius: 'var(--radius-pill)', border: 'none', padding: 0, cursor: 'pointer',
              background: form.published ? 'var(--brand-primary)' : 'var(--border-strong)',
              transition: 'background var(--duration-fast) var(--ease-standard)',
            }}
          >
            <span
              style={{
                position: 'absolute', top: 3, left: form.published ? 19 : 3, width: 18, height: 18, borderRadius: '50%',
                background: 'var(--surface-1)', boxShadow: 'var(--shadow-md)', transition: 'left var(--duration-fast) var(--ease-standard)',
              }}
            />
          </button>
        </div>

        {/* ── Proof of life: the vacancy really is on the public page ── */}
        {form.published && !dirty && (
          <Note tone="success">
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span>
                Live on the Recruit landing page
                {publishedAt ? ` since ${new Date(publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}.
              </span>
              <a
                href={CAREERS_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: 600, color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                See it live <Icon name="eye" size={14} />
              </a>
            </span>
          </Note>
        )}
        {!form.published && paused.paused && !dirty && (
          <Note tone="warning">
            Paused{paused.at ? ` on ${new Date(paused.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
            {paused.by ? ` by ${paused.by}` : ''}. Off the careers page and not taking applications; candidates,
            pipeline and description are kept. Turn it back on to reopen it.
          </Note>
        )}
        {form.published && closed && (
          <Note tone="warning">
            Applications closed on {longDay(form.closingDate)}. The vacancy is no longer on the careers page.
            Set a later closing date to reopen it.
          </Note>
        )}
        {form.published && dirty && (
          <p style={caption}>
            Changes reach the public page when you save.
          </p>
        )}

        {/* ── Listing detail ─────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          <FormField label="Posts available">
            <input
              type="number"
              min={1}
              aria-label="Posts available"
              style={nativeInput}
              value={form.openings}
              onChange={(e) => set({ openings: e.target.value })}
            />
          </FormField>
          {field('Category', 'category', { placeholder: 'e.g. TGT' })}
          {field('Grade band', 'gradeBand', { placeholder: 'e.g. Grade III – VI' })}
          {field('Search tags', 'tags', { placeholder: 'mathematics numeracy olympiad' })}
          {/* Teaching / non-teaching / leadership (pr_487b5f4b2e). Not the
              category, which places the tile; blank = not stated. */}
          <FormField label="Track">
            <select
              aria-label="Track"
              style={nativeInput}
              value={form.track}
              onChange={(e) => set({ track: e.target.value })}
            >
              {TRACKS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </FormField>
          <FormField
            label="Applications close on"
            hint={form.closingDate ? `Taking applications until the end of ${longDay(form.closingDate)} (India time).` : 'No closing date — open until you pause it.'}
          >
            <input
              type="date"
              aria-label="Applications close on"
              style={nativeInput}
              value={form.closingDate}
              min={today}
              onChange={(e) => set({ closingDate: e.target.value })}
            />
          </FormField>
        </div>
        <p style={caption}>
          Category groups the tile on the careers page; grade band and tags are shown and searched on it. Track says which kind of post it is (teaching, non-teaching or leadership) and is not shown on the page.
        </p>

        {/* ── Warning when publishing without a description ──────────── */}
        {form.published && !hasDescription && (
          <Note tone="warning">
            A vacancy cannot be published without a written job description — the school's
            rule is that the public page never advertises a post it cannot describe. Write
            the description below, then publish.
          </Note>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button onClick={save} disabled={saving || !dirty} icon={<Icon name={saving ? 'spinner' : 'check-circle'} spin={saving} />}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          {dirty && !saving && (
            <span style={caption}>Unsaved changes</span>
          )}
        </div>
      </div>
    </Card>
  );
}
